import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEffect, useState, FormEvent } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/iletisim';
const pageUrl = `${siteUrl}${pagePath}`;
const formSubmitUrl = 'https://readdy.ai/api/form/d9qseq7nahb7382oves0';

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      name: 'BIFF Digital İletişim',
      description:
        'BIFF Digital ile iletişime geçin. Ankara merkezli growth & campaign agency. Markanızın büyüme sistemini konuşalım.',
      url: pageUrl,
      about: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        email: 'info@biffdigital.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'İletişim', item: pageUrl },
      ],
    },
  ],
};

const hizmetOptions = [
  'Performans Pazarlama',
  'SEO & GEO',
  'Marka Stratejisi & Branding',
  'Web Geliştirme & Yazılım',
  'Nöropazarlama & Davranışsal Analiz',
  'Kampanya & İtibar Yönetimi',
  'E-Ticaret',
  'Endüstriyel & B2B',
  'Diğer / Birden Fazla',
];

export default function Iletisim() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = 'İletişim | BIFF Digital - Ankara Dijital Pazarlama Ajansı';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot check
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !(value as string).trim()) {
          params.append(key, value as string);
        }
      });

      const res = await fetch(formSubmitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string; detail?: string } }).meta?.message ||
          (parsed as { meta?: { message?: string; detail?: string } }).meta?.detail ||
          (parsed as { message?: string }).message ||
          responseText ||
          'Bir hata oluştu, lütfen tekrar deneyin.';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">İLETİŞİM</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-6">
              Bir Probleminiz Var.<br />
              <span className="text-primary-500">Konuşalım.</span>
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-8" />
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-xl">
              Markanızın nerede tıkandığını, hangi fırsatı kaçırdığını veya nasıl büyüyebileceğini konuşmak için bize yazın. İlk analiz görüşmesi ücretsizdir.
            </p>
          </div>
        </section>

        {/* Form + Info */}
        <section className="section-padding">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">

              {/* Form */}
              <div className="lg:col-span-3">
                <div className="glass-card rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl font-bold text-foreground-50 mb-6">Görüşme Talep Et</h2>

                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-2xl text-primary-500" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground-50 mb-2">Mesajınız iletildi.</h3>
                      <p className="text-sm text-foreground-400">
                        En kısa sürede size dönüş yapacağız.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="ad_soyad" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            Ad Soyad <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="ad_soyad"
                            name="ad_soyad"
                            required
                            className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="Adınız ve soyadınız"
                          />
                        </div>
                        <div>
                          <label htmlFor="sirket" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            Şirket
                          </label>
                          <input
                            type="text"
                            id="sirket"
                            name="sirket"
                            className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="Şirket adı"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            E-posta <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="ornek@email.com"
                          />
                        </div>
                        <div>
                          <label htmlFor="telefon" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            Telefon
                          </label>
                          <input
                            type="tel"
                            id="telefon"
                            name="telefon"
                            className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="+90 5XX XXX XX XX"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="web_sitesi" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          Web Sitesi
                        </label>
                        <input
                          type="url"
                          id="web_sitesi"
                          name="web_sitesi"
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="https://www.sirketiniz.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="hizmet" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          İlgilenilen Hizmet <span className="text-primary-500">*</span>
                        </label>
                        <select
                          id="hizmet"
                          name="hizmet"
                          required
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 focus:outline-none focus:border-primary-500/50 transition-colors appearance-none cursor-pointer"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 16px center',
                          }}
                        >
                          <option value="" className="bg-background-100">Seçiniz</option>
                          {hizmetOptions.map((opt) => (
                            <option key={opt} value={opt} className="bg-background-100">{opt}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="marka_bilgisi" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          Marka hakkında kısa bilgi <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="marka_bilgisi"
                          name="marka_bilgisi"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                          placeholder="Markanız, hedefleriniz ve ihtiyaçlarınız hakkında kısaca bilgi verin..."
                        />
                        <p className="text-xs text-foreground-600 mt-1">En fazla 500 karakter</p>
                      </div>

                      {/* Anti-spam honeypot */}
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="absolute opacity-0 pointer-events-none"
                        style={{ position: 'absolute', left: '-9999px' }}
                      />

                      {formStatus === 'error' && (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <p className="text-sm text-red-400">{formError}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="btn-primary w-full justify-center text-base py-4 whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            Gönderiliyor...
                          </>
                        ) : (
                          <>
                            Görüşme Talep Et
                            <i className="ri-arrow-right-line" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-card rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-foreground-50 mb-5">İletişim Bilgileri</h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-background-200 rounded-xl flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-foreground-300" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-200">Adres</p>
                        <p className="text-sm text-foreground-400 leading-relaxed">
                          Söğütözü Cad. No: 2 Koç Kuleleri<br />C Blok 17, Çankaya/Ankara
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-background-200 rounded-xl flex items-center justify-center shrink-0">
                        <i className="ri-phone-line text-foreground-300" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-200">Telefon</p>
                        <a href="tel:+905557101968" className="text-sm text-foreground-400 hover:text-primary-500 transition-colors">
                          +90 555 710 1968
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-background-200 rounded-xl flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-foreground-300" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-200">E-posta</p>
                        <a href="mailto:info@biffdigital.com" className="text-sm text-foreground-400 hover:text-primary-500 transition-colors">
                          info@biffdigital.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-background-200 rounded-xl flex items-center justify-center shrink-0">
                        <i className="ri-time-line text-foreground-300" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-200">Çalışma Saatleri</p>
                        <p className="text-sm text-foreground-400">Pazartesi - Cuma: 09:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="glass-card rounded-2xl overflow-hidden">
                  <iframe
                    title="BIFF Digital Ankara Ofis Konumu - Koç Kuleleri Söğütözü"
                    src="https://www.google.com/maps?q=39.912360,32.803759&z=17&hl=tr&output=embed"
                    width="100%"
                    height="240"
                    className="border-0"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=39.912360,32.803759"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-background-100 border-t border-background-200/60 text-sm font-medium text-foreground-300 hover:text-primary-500 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-navigation-line" />
                    Yol Tarifi Al
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}